import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    fetchEcacDepartureSummaryReport,
    resetEcacDepartureSummaryReport,
    selectEcacDepartureSummaryReportData,
} from '_redux/slices/sla-reports/ecac-departure-summary-report';
import {
    fetchEcacArrivalSummaryReport,
    resetEcacArrivalSummaryReport,
    selectEcacArrivalSummaryReportData,
} from '_redux/slices/sla-reports/ecac-arrival-summary-report';
import CorkSlaTable from './cork-sla-table';

const SUB_REPORTS = [
    {
        key: 'departure',
        label: 'ECAC Departure Summary',
        thunk: fetchEcacDepartureSummaryReport,
        reset: resetEcacDepartureSummaryReport,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
    {
        key: 'arrival',
        label: 'ECAC Arrival Summary',
        thunk: fetchEcacArrivalSummaryReport,
        reset: resetEcacArrivalSummaryReport,
        buildParams: (base) => ({ ...base, hideBreakdown: true }),
    },
];

const toLocalDateString = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const CorkSlaReport = ({ dateRange, iata, runReportRef, onRunLoadingChange }) => {
    const dispatch = useDispatch();
    const cancelledRef = useRef(false);

    const [currentIndex, setCurrentIndex] = useState(null);
    const [errors, setErrors] = useState({});
    const [done, setDone] = useState(false);
    const [completedKeys, setCompletedKeys] = useState({});

    const departureData = useSelector(selectEcacDepartureSummaryReportData);
    const arrivalData = useSelector(selectEcacArrivalSummaryReportData);

    const isRunning = currentIndex !== null;

    const handleRunReport = useCallback(async () => {
        const startDate = dateRange?.[0];
        const endDate = dateRange?.[1];
        if (!startDate || !endDate || !iata) return;

        const formattedStartDate = toLocalDateString(startDate);
        const formattedEndDate = toLocalDateString(endDate);
        const baseParams = { startDate: formattedStartDate, endDate: formattedEndDate, iata };

        cancelledRef.current = false;
        setErrors({});
        setDone(false);
        setCompletedKeys({});

        for (let i = 0; i < SUB_REPORTS.length; i++) {
            if (cancelledRef.current) return;
            setCurrentIndex(i);
            const report = SUB_REPORTS[i];
            try {
                await dispatch(report.thunk(report.buildParams(baseParams))).unwrap();
                setCompletedKeys((prev) => ({ ...prev, [report.key]: true }));
            } catch (e) {
                setErrors((prev) => ({ ...prev, [report.key]: e?.message ?? 'Failed' }));
                setCompletedKeys((prev) => ({ ...prev, [report.key]: true }));
            }
        }

        if (!cancelledRef.current) {
            setCurrentIndex(null);
            setDone(true);
        }
    }, [dispatch, dateRange, iata]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isRunning);
    }, [isRunning, onRunLoadingChange]);

    useEffect(() => {
        return () => {
            cancelledRef.current = true;
            SUB_REPORTS.forEach((r) => dispatch(r.reset()));
        };
    }, [dispatch]);

    const progressPercent = isRunning
        ? Math.round(((currentIndex + 1) / SUB_REPORTS.length) * 100)
        : done ? 100 : 0;

    return (
        <div>
            {(isRunning || (!done && progressPercent === 0)) && (
                <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '20px',
                    marginBottom: '24px',
                    backgroundColor: '#f9fafb',
                }}>
                    {isRunning && (
                        <>
                            <p style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937', marginTop: 0, marginBottom: '12px' }}>
                                Fetching report {currentIndex + 1} of {SUB_REPORTS.length}: {SUB_REPORTS[currentIndex].label}
                            </p>
                            <div style={{
                                width: '100%',
                                height: '8px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                marginBottom: '16px',
                            }}>
                                <div style={{
                                    width: `${progressPercent}%`,
                                    height: '100%',
                                    backgroundColor: '#3b82f6',
                                    borderRadius: '4px',
                                    transition: 'width 0.3s ease',
                                }} />
                            </div>
                        </>
                    )}
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {SUB_REPORTS.map((report, idx) => {
                            let status = 'pending';
                            if (errors[report.key]) status = 'error';
                            else if (completedKeys[report.key] && !errors[report.key]) status = 'done';
                            else if (isRunning && idx === currentIndex) status = 'loading';

                            const icon = {
                                pending: '\u25CB',
                                loading: '\u25CF',
                                done: '\u2713',
                                error: '\u2717',
                            }[status];

                            const color = {
                                pending: '#9ca3af',
                                loading: '#3b82f6',
                                done: '#16a34a',
                                error: '#dc2626',
                            }[status];

                            return (
                                <li key={report.key} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '4px 0',
                                    fontSize: '13px',
                                    color,
                                    fontWeight: status === 'loading' ? 600 : 400,
                                }}>
                                    <span style={{ fontSize: '14px', width: '16px', textAlign: 'center' }}>{icon}</span>
                                    {report.label}
                                    {errors[report.key] && (
                                        <span style={{ fontSize: '12px', color: '#dc2626', marginLeft: '4px' }}>
                                            — {errors[report.key]}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {done && (
                <>
                    {(errors.departure && errors.arrival) ? (
                        <p style={{ fontSize: '13px', color: '#dc2626' }}>
                            Failed to load both departure and arrival reports.
                        </p>
                    ) : (
                        <CorkSlaTable
                            departureSummary={departureData?.summary ?? null}
                            arrivalSummary={arrivalData?.summary ?? null}
                            startDate={departureData?.startDate ?? arrivalData?.startDate}
                            endDate={departureData?.endDate ?? arrivalData?.endDate}
                            departureError={errors.departure}
                            arrivalError={errors.arrival}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default CorkSlaReport;
